import useAuthStore from 'src/auth/store/firebase/auth-store';
import { useDeviceContextStore } from 'src/store/DeviceContext';

import { getForceLogoutControlState, saveForceLogoutControlState } from './DB_Utils';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ForceLogoutTarget = {
  businessLocationId?: string;
  terminalId?: string;
  deviceId?: string; // matches capacitorDeviceId
  userId?: string; // matches Firebase UID
  allDevices?: boolean;
};

type RemoteForceLogout = {
  enabled: boolean;
  version: number;
  updatedAt?: unknown;
  reason?: string;
  target?: ForceLogoutTarget;
};

type CurrentDeviceContext = {
  businessId: string;
  userId?: string | null;
  capacitorDeviceId?: string | null;
  terminalId?: string | null;
  businessLocationId?: string | null;
};

// ---------------------------------------------------------------------------
// Target matching
// ---------------------------------------------------------------------------

function doesForceLogoutTargetCurrentDevice(
  target: ForceLogoutTarget | undefined,
  ctx: CurrentDeviceContext
): boolean {
  if (!target) return false;
  if (target.allDevices) return true;
  if (target.deviceId && target.deviceId === ctx.capacitorDeviceId) return true;
  if (target.terminalId && target.terminalId === ctx.terminalId) return true;
  if (target.businessLocationId && target.businessLocationId === ctx.businessLocationId)
    return true;
  if (target.userId && target.userId === ctx.userId) return true;
  return false;
}

// ---------------------------------------------------------------------------
// Current device context resolution
// ---------------------------------------------------------------------------

async function resolveCurrentDeviceContext(businessId: string): Promise<CurrentDeviceContext> {
  const deviceContext = useDeviceContextStore.getState();
  const { user } = useAuthStore.getState();

  return {
    businessId,
    userId: user?.uid ?? null,
    capacitorDeviceId: deviceContext.deviceFingerprint,
    terminalId: deviceContext.terminalId,
    businessLocationId: deviceContext.businessLocationId,
  };
}

// ---------------------------------------------------------------------------
// Field handler — called by the business document listener on every snapshot
// ---------------------------------------------------------------------------

export async function handleDeviceControlBusinessField(params: {
  businessId: string;
  deviceControl: unknown;
}): Promise<void> {
  const { businessId, deviceControl } = params;

  if (!deviceControl || typeof deviceControl !== 'object') return;

  const dc = deviceControl as { forceLogout?: unknown };
  const fl = dc.forceLogout;

  if (!fl || typeof fl !== 'object') return;

  const remoteForceLogout = fl as Partial<RemoteForceLogout>;

  if (remoteForceLogout.enabled !== true) return;
  if (typeof remoteForceLogout.version !== 'number') return;

  const remoteVersion = remoteForceLogout.version;
  const ctx = await resolveCurrentDeviceContext(businessId);

  if (!doesForceLogoutTargetCurrentDevice(remoteForceLogout.target, ctx)) return;

  const localState = await getForceLogoutControlState();

  // First-time baseline: record the current version as already seen so an
  // old active command never traps a freshly logged-in user.
  if (!localState) {
    await saveForceLogoutControlState({
      businessId,
      deviceIdOrUserId: ctx.capacitorDeviceId ?? ctx.userId ?? 'unknown',
      version: remoteVersion,
      initializedAt: new Date().toISOString(),
      status: 'initialized',
    });
    return;
  }

  // Already handled this version or an older one — do nothing.
  if (remoteVersion <= localState.version) return;

  // New command — update local state and show the dialog.
  await saveForceLogoutControlState({
    ...localState,
    version: remoteVersion,
    shownAt: new Date().toISOString(),
    status: 'shown',
  });

  // Lazy import to avoid circular dependency between store and service.
  const { useSystemControlStore } = await import('src/store/system-control');
  useSystemControlStore.getState().showForceLogoutDialog({
    businessId,
    version: remoteVersion,
    reason: remoteForceLogout.reason,
    deviceId: ctx.capacitorDeviceId ?? undefined,
    terminalId: ctx.terminalId ?? undefined,
    businessLocationId: ctx.businessLocationId ?? undefined,
    userId: ctx.userId ?? undefined,
  });
}
